import { Injectable, signal } from '@angular/core';
import { BlipChat } from 'blip-chat-widget';
import { UserInfo } from '@app/store/session/session.store';
import { RoleEnumNames } from '@app/views/private/admin/model/admin-users';

const SUPPORT_ICON_PATH = 'assets/images/icons/icone-suporte-navy-512.png';
const TOOLTIP_STORAGE_KEY = 'aa-blip-support-tooltip-last-seen';
const TOOLTIP_DELAY_MS = 3000;

@Injectable({
  providedIn: 'root'
})
export class BlipChatService {
  private widget: BlipChat | null = null;
  private contextKey: string | null = null;
  private readonly activeState = signal(false);
  private readonly tooltipState = signal(false);
  private tooltipTimer: ReturnType<typeof setTimeout> | null = null;

  readonly isTooltipVisible = this.tooltipState.asReadonly();

  isActive(): boolean {
    return this.activeState();
  }

  init(user?: UserInfo): void {
    if (!this.shouldEnableForUser(user)) {
      return;
    }

    const nextContextKey = this.buildContextKey(user);
    if (this.widget && this.contextKey === nextContextKey) {
      return;
    }

    this.destroy();

    this.widget = this.createWidget(user);
    this.widget.build();
    this.contextKey = nextContextKey;
    this.activeState.set(true);
    this.showTooltip();
  }

  close(): void {
    this.destroy();
  }

  destroy(): void {
    this.hideTooltip();
    this.widget?.destroy();
    this.widget = null;
    this.contextKey = null;
    this.activeState.set(false);
  }

  openChat(): void {
    this.hideTooltip();
    this.widget?.toogleChat();
  }

  dismissTooltip(): void {
    this.hideTooltip();
    this.markSeenToday();
  }

  private shouldEnableForUser(user?: UserInfo): user is UserInfo {
    return Boolean(user?.dealerId || user?.oemDealerId);
  }

  private buildContextKey(user: UserInfo): string {
    return [
      user.id,
      user.dealerId,
      user.oemDealerId,
      user.accessProfile?.name
    ].join('|');
  }

  private createWidget(user: UserInfo): BlipChat {
    const linkPage = window.location.href;
    const isOemDealer = user.oemDealerId !== undefined;
    const titleColorAlt = getComputedStyle(document.documentElement)
      .getPropertyValue('--title-color-alt')
      .trim() || '#1c2e42';

    const customStyle = `
      #blip-chat-header {
        background-color: ${titleColorAlt} !important;
      }

      .blip-chat-header {
        background-color: ${titleColorAlt} !important;
      }

      #message-input {
        border-color: ${titleColorAlt} !important;
      }

      .send-button {
        background-color: ${titleColorAlt} !important;
      }

      .bubble.right,
      div.bubble.right,
      .bubble-container .bubble.right {
        background-color: ${titleColorAlt} !important;
        background: ${titleColorAlt} !important;
      }

      .bubble.right::before {
        background-color: ${titleColorAlt} !important;
      }
    `;

    return new BlipChat()
      .withAppKey('cGxhdGFmb3JtYTozMTM5OGUwZC0zMTZhLTQyZjItOGM3Ni05M2QwMGIxYmE0NmM=')
      .withAccount({
        fullName: user.name,
        email: user.email,
        city: user.city,
        extras: {
          vendedor: user.oemDealerName,
          idVendedor: user.oemDealerId,
          comprador: user.dealerName,
          idComprador: user.dealerId,
          paginaOrigem: linkPage,
          destino: isOemDealer ? 'FILA VENDEDOR' : 'FILA COMPRADOR',
          antigasPermissoes: user.roles.map(role => RoleEnumNames[role]).join(', '),
          novaPermissao: user.accessProfile?.name,
          unidade: user.evalDealerName,
          idUnidade: user.evalDealerId
        }
      })
      .withButton({
        color: '#ffffff',
        icon: SUPPORT_ICON_PATH
      })
      .withEventHandler(BlipChat.ENTER_EVENT, () => {
        this.hideTooltip();
      })
      .withEventHandler(BlipChat.LEAVE_EVENT, () => {
        this.showTooltip();
      })
      .withCustomStyle(customStyle)
      .withCustomCommonUrl('https://superbid.chat.blip.ai/');
  }

  private showTooltip(): void {
    this.hideTooltip();

    if (this.wasSeenToday()) {
      return;
    }

    this.tooltipTimer = setTimeout(() => {
      this.tooltipState.set(true);
      this.tooltipTimer = null;
    }, TOOLTIP_DELAY_MS);
  }

  private hideTooltip(): void {
    if (this.tooltipTimer) {
      clearTimeout(this.tooltipTimer);
      this.tooltipTimer = null;
    }

    this.tooltipState.set(false);
  }

  private wasSeenToday(): boolean {
    try {
      return localStorage.getItem(TOOLTIP_STORAGE_KEY) === this.getTodayKey();
    } catch {
      return false;
    }
  }

  private markSeenToday(): void {
    try {
      localStorage.setItem(TOOLTIP_STORAGE_KEY, this.getTodayKey());
    } catch {
      
    }
  }

  private getTodayKey(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}